import clsx from 'clsx';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useLiveGoldPrice } from '@/api/avail-loan';
import { useUserStore } from '@/common/store';
import useMoengageStore from '@/store/moengage';
import { calculateLTV, StringPatterns } from '@/utils';
import { createMoengageEvent } from '@/utils/moengage';

import goldLoanIcon from '../../../assets/images/icons/gold-coin-icon.svg';
import rupeeIcon from '../../../assets/images/icons/rupee-icon.svg';
import GlNewNote from './GlNewNote';

type LTVForm = {
  loanAmountInput: string;
  goldLoanInput: string;
};

const LTVCalcualator = () => {
  const [loanAmountOption, setLoanAmountOption] = useState<boolean>(true);
  const router = useRouter();

  const { mobileNumber, glApplyNowClicked } = useMoengageStore((state) => {
    return {
      mobileNumber: state.mobileNumber,
      glApplyNowClicked: state.glApplyNowClicked,
    };
  });

  const {
    handleSubmit: loanSubmit,
    register: loanRegister,
    watch,
    formState: { errors: loanErrors },
  } = useForm({
    defaultValues: {
      loanAmountInput: '',
      goldLoanInput: '',
    },
  });

  const { liveGoldPrice } = useLiveGoldPrice();
  const saveInputs = useUserStore((state) => state.setInputs);

  const onClickSubmit = (data: LTVForm) => {
    createMoengageEvent(glApplyNowClicked, {
      created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
      mobile_number: mobileNumber,
      is_login: false,
      in_grams: !loanAmountOption,
      in_rupee: loanAmountOption,
      selected_amount: data.loanAmountInput,
      selected_weight: data.goldLoanInput,
      live_gold_price: liveGoldPrice,
      live_buying_price: liveGoldPrice,
      service_type: 'GL',
    });

    saveInputs({
      selectedOption: loanAmountOption ? 'amount' : 'weight',
      requestedValue: loanAmountOption
        ? data.loanAmountInput
        : data.goldLoanInput,
    }).then(() => {
      // TODO: first get signed in if not
      router.push('/gold-loans/new-loan');
    });
  };

  const handleLoanAmountClick = () => {
    setLoanAmountOption(true);
  };

  const handleGoldAmountClick = () => {
    setLoanAmountOption(false);
  };

  return (
    <section className="bg bg--light pt-0">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-6">
            <h2 className="text--black section__title">
              {/* Get Customised Loan Scheme against your
              <span className="text--yellow"> Gold</span> */}
              Know Your Gold’s Worth, Instantly
            </h2>
            <p className="section__desc text--black">
              {/* Estimate the monetary value of your gold with our calculator and
              get a customised loan scheme */}
              Use our Gold Loan Calculator to unlock the value of your gold,
              right here, right now.
            </p>
          </div>
          <div className="col-lg-6 pledge visitSchedule">
            <div className="loanDetails__card loanCard">
              <div className="loanRequirements">
                <p className="loanCard__title text--bold text--black">
                  Gold loan Calculator
                </p>
                <div className="livePrice">
                  <p className="liveChips text--semi text--white">
                    <span className="pulsingDot"></span> Live
                  </p>
                  <p className="text--regular text--black title">
                    Loan to Value:{' '}
                    <span className="text--bold">₹{liveGoldPrice}/gm**</span>
                  </p>
                </div>
                <div className="radio__container">
                  <label className="radio__circle">
                    <input
                      type="radio"
                      name="loan"
                      defaultChecked
                      onClick={handleLoanAmountClick}
                    />
                    <span className="radio__circle-checkmark"></span>
                    <p className="text--regular text--black radio__circle-label">
                      Required Loan Amount
                    </p>
                  </label>
                  <label className="radio__circle">
                    <input
                      type="radio"
                      name="loan"
                      onClick={handleGoldAmountClick}
                    />
                    <span className="radio__circle-checkmark"></span>
                    <p className="text--regular text--black radio__circle-label">
                      My Gold Weight
                    </p>
                  </label>
                </div>
                <form noValidate>
                  {loanAmountOption ? (
                    <>
                      <div
                        className={clsx(
                          'input-container',
                          loanErrors.loanAmountInput?.message && 'inValid'
                        )}
                      >
                        <div className="input-form justify-content-between">
                          <span className="text--new--black">₹</span>
                          <input
                            type="text"
                            placeholder="Enter amount"
                            {...loanRegister('loanAmountInput', {
                              required: 'Should not be empty',
                              min: {
                                value: 1000,
                                message: 'Minimum value is ₹1000',
                              },
                              pattern: {
                                value: StringPatterns.numberOnly,
                                message: 'Add valid amount',
                              },
                            })}
                          />
                        </div>
                        {/* <div className="input-container inValid"> */}
                        <p className="text--red text--regular errorMessage">
                          {loanErrors.loanAmountInput?.message}
                        </p>
                        {/* </div> */}
                      </div>
                      <div className="requiredGold">
                        <p className="text--black text--semi title">
                          <Image
                            style={{ color: undefined }}
                            className="img-fluid"
                            src={goldLoanIcon}
                            alt="gold coin icon"
                          />
                          Min. gold required for the loan*
                        </p>
                        <p className="text--black text--semi">
                          {' '}
                          {calculateLTV(
                            parseFloat(watch('loanAmountInput')),
                            liveGoldPrice,
                            'AMOUNT-TO-GOLD'
                          )}{' '}
                          gms
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className={clsx(
                          'input-container',
                          loanErrors.loanAmountInput?.message && 'inValid'
                        )}
                      >
                        <div className="input-form justify-content-between">
                          <input
                            type="text"
                            {...loanRegister('goldLoanInput', {
                              required: 'Should not be empty',
                              validate: (value: string) => {
                                if (parseFloat(value) * liveGoldPrice < 1000) {
                                  return 'Minimum value is ₹1000';
                                }
                                return true;
                              },
                              pattern: {
                                value: StringPatterns.floatOnly,
                                message: 'Add valid weight',
                              },
                            })}
                            placeholder="Enter Gold in gms"
                          />
                          <span className="text--semi">gms</span>
                        </div>
                        {/* <div className="input-container inValid"> */}
                        <p className="text--red text--regular errorMessage">
                          {loanErrors.goldLoanInput?.message}
                        </p>
                        {/* </div> */}
                      </div>
                      <div className="requiredGold">
                        <p className="text--black text--semi title">
                          <Image
                            style={{ color: undefined }}
                            className="img-fluid"
                            src={rupeeIcon}
                            alt="gold coin icon"
                          />
                          Max eligible loan amount
                        </p>
                        <p className="text--black text--semi">
                          ₹
                          {calculateLTV(
                            parseFloat(watch('goldLoanInput')),
                            liveGoldPrice,
                            'GOLD-TO-AMOUNT'
                          )}{' '}
                        </p>
                      </div>
                    </>
                  )}
                </form>

                <div className="action-container">
                  <a
                    href=""
                    onClick={(e) => {
                      e.preventDefault();

                      loanSubmit(onClickSubmit)();
                    }}
                    // added css and state to disable this anchor tag outside the form
                    className={`custom__button custom__button--dark text--bold position-relative`}
                    id="gold-loans_apply-now"
                  >
                    Apply Now
                  </a>
                </div>
                <GlNewNote />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LTVCalcualator;
